/**
 * Renders the tree from `markdown.ts` as Preact elements. Text only ever
 * reaches the DOM as text nodes — no `dangerouslySetInnerHTML` anywhere — so
 * this stays safe whatever the model writes. Styles: `.mrw-md` in widget.css.
 */
import { h, type ComponentChildren } from 'preact';
import { useMemo } from 'preact/hooks';

import { parseMarkdown, type Block, type Inline } from './markdown';

function inline(nodes: Inline[]): ComponentChildren[] {
  return nodes.map((n, k) => {
    if (typeof n === 'string') return n;
    switch (n.t) {
      case 'br':
        return <br key={k} />;
      case 'code':
        return <code key={k}>{n.v}</code>;
      case 'strong':
        return <strong key={k}>{inline(n.c)}</strong>;
      case 'em':
        return <em key={k}>{inline(n.c)}</em>;
      case 'del':
        return <del key={k}>{inline(n.c)}</del>;
      case 'link':
        return n.href ? (
          <a key={k} href={n.href} target="_blank" rel="noopener noreferrer">
            {inline(n.c)}
          </a>
        ) : (
          <span key={k}>{inline(n.c)}</span>
        );
    }
  });
}

function blocks(list: Block[]): ComponentChildren[] {
  return list.map((b, k) => {
    switch (b.t) {
      case 'p':
        return <p key={k}>{inline(b.c)}</p>;
      case 'h':
        return h(`h${b.level}`, { key: k }, inline(b.c));
      case 'pre':
        return (
          <pre key={k}>
            <code>{b.v}</code>
          </pre>
        );
      case 'hr':
        return <hr key={k} />;
      case 'quote':
        return <blockquote key={k}>{blocks(b.c)}</blockquote>;
      case 'list': {
        const items = b.items.map((item, j) => <li key={j}>{blocks(item)}</li>);
        return b.ordered ? (
          <ol key={k} start={b.start}>
            {items}
          </ol>
        ) : (
          <ul key={k}>{items}</ul>
        );
      }
      case 'table':
        return (
          <div key={k} class="mrw-md-table">
            <table>
              <thead>
                <tr>
                  {b.head.map((cell, j) => (
                    <th key={j} style={b.align[j] ? { textAlign: b.align[j] } : undefined}>
                      {inline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, j) => (
                      <td key={j} style={b.align[j] ? { textAlign: b.align[j] } : undefined}>
                        {inline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}

export function MarkdownView({ text }: { text: string }) {
  // Memoised per message: while one reply streams, every other bubble
  // re-renders with unchanged text and skips the re-parse.
  const tree = useMemo(() => blocks(parseMarkdown(text)), [text]);
  return <div class="mrw-md">{tree}</div>;
}
